import { isApiError, USER_FIELD_MAX, type User } from "@safers/api";
import { useProfileForm } from "@safers/auth";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  ProfileEditCard,
  type ProfileEditCardProps,
} from "@safers/ui/organisms";
import { useToastStore } from "@/stores";

export function UserIdentity({ user }: { user: User }) {
  const initials = (user.name ?? user.username ?? "?").slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-3">
      <span
        className="inline-flex size-12 flex-none items-center justify-center rounded-full text-sm font-bold"
        style={{
          background: "var(--brand-primary-soft)",
          color: "var(--brand-primary-on-soft)",
        }}
      >
        {initials}
      </span>
      <div className="min-w-0">
        <p className="truncate text-base font-bold text-[var(--text-primary)]">
          {user.name ?? user.username}
        </p>
        <p className="truncate font-mono text-xs text-[var(--text-tertiary)]">{user.username}</p>
      </div>
    </div>
  );
}

export function ProfileForm({
  user,
  details,
  onDone,
}: {
  user: User;
  details?: ProfileEditCardProps["details"];
  onDone: () => void;
}) {
  const form = useProfileForm(user, {
    onSaved: () => {
      useToastStore.getState().toast.success("내 정보를 저장했습니다.");
      onDone();
    },
    onError: (err) => {
      useToastStore
        .getState()
        .toast.error(
          isApiError(err) && !err.isServerError ? err.message : "내 정보 저장에 실패했습니다."
        );
    },
  });

  return (
    <ProfileEditCard
      values={form.values}
      maxLengths={USER_FIELD_MAX}
      details={details}
      errors={form.errors}
      onChange={form.change}
      onSubmit={form.submit}
      isLoading={form.isSaving}
      canSubmit={form.isDirty}
    />
  );
}

export function ProfileModal({
  user,
  open,
  onOpenChange,
}: {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-md">
        <ModalHeader>
          <ModalTitle>내 정보</ModalTitle>
          <ModalDescription>이름, 부서, 연락처를 고칠 수 있습니다.</ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-4 pt-4">
          <UserIdentity user={user} />
          <ProfileForm
            user={user}
            details={[
              { label: "사번", value: user.code || "—" },
              { label: "권한", value: user.authority ?? "USER" },
            ]}
            onDone={() => onOpenChange(false)}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
